import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import { uploadFile, uploadMultipleFiles } from '../../services/upload';
import './Ads.css';

const defaultCrop = {
  enabled: false,
  x: 0,
  y: 0,
  width: 100,
  height: 100
};

const emptyProductItem = () => ({
  name: '',
  unit: '',
  price: '',
  originalPrice: '',
  offerPrice: '',
  image: '',
  keywords: '',
  tags: ''
});

const createEmptyForm = () => ({
  adType: 'poster',
  categoryId: '',
  productId: '',
  productName: '',
  productKeywords: '',
  productTags: '',
  title: '',
  description: '',
  mainImageUrl: '',
  posterImage: '',
  productImage: '',
  subImageUrls: '',
  originalPrice: '',
  offerPrice: '',
  durationDays: 10,
  startDate: '',
  productItems: [emptyProductItem()]
});

const readFilePreview = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.readAsDataURL(file);
});

const loadImage = (source) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = source;
});

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeCrop = (crop) => {
  const x = clamp(Number(crop.x) || 0, 0, 95);
  const y = clamp(Number(crop.y) || 0, 0, 95);
  const width = clamp(Number(crop.width) || 100, 5, 100 - x);
  const height = clamp(Number(crop.height) || 100, 5, 100 - y);

  return {
    enabled: Boolean(crop.enabled),
    x,
    y,
    width,
    height
  };
};

const cropImageFile = async (file, crop) => {
  if (!file || !crop?.enabled) {
    return file;
  }

  const preview = await readFilePreview(file);
  const image = await loadImage(preview);
  const xPercent = clamp(Number(crop.x) || 0, 0, 95);
  const yPercent = clamp(Number(crop.y) || 0, 0, 95);
  const widthPercent = clamp(Number(crop.width) || 100, 5, 100 - xPercent);
  const heightPercent = clamp(Number(crop.height) || 100, 5, 100 - yPercent);

  if (xPercent === 0 && yPercent === 0 && widthPercent === 100 && heightPercent === 100) {
    return file;
  }

  const sourceX = Math.round((image.naturalWidth * xPercent) / 100);
  const sourceY = Math.round((image.naturalHeight * yPercent) / 100);
  const sourceWidth = Math.round((image.naturalWidth * widthPercent) / 100);
  const sourceHeight = Math.round((image.naturalHeight * heightPercent) / 100);

  const canvas = document.createElement('canvas');
  canvas.width = Math.min(1400, Math.round(sourceWidth));
  canvas.height = Math.round(canvas.width * (sourceHeight / sourceWidth));
  const context = canvas.getContext('2d');
  context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, file.type || 'image/jpeg', 0.9));
  if (!blob) return file;
  return new File([blob], file.name, { type: blob.type || file.type });
};

const splitValues = (value) => String(value || '')
  .split(/[\n,]/)
  .map((item) => item.trim())
  .filter(Boolean);

const productToFormItem = (product = {}) => ({
  name: product.name || '',
  unit: product.unit || '',
  price: product.price ?? '',
  originalPrice: product.metadata?.originalPrice ?? '',
  offerPrice: product.metadata?.offerPrice ?? product.price ?? '',
  image: product.image || '',
  keywords: Array.isArray(product.keywords) ? product.keywords.join(', ') : '',
  tags: Array.isArray(product.tags) ? product.tags.join(', ') : ''
});

const ShopAdminAds = () => {
  const [ads, setAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [filters, setFilters] = useState({ status: '' });
  const [formData, setFormData] = useState(createEmptyForm);
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState('');
  const [mainImageCrop, setMainImageCrop] = useState(defaultCrop);
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState('');
  const [productImageCrop, setProductImageCrop] = useState(defaultCrop);
  const [subImageFiles, setSubImageFiles] = useState([]);
  const [subImagePreviews, setSubImagePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [modalMessage, setModalMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchCategories();
    fetchSubscription();
  }, []);

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const selectedCategoryName = useMemo(() => {
    const category = categories.find((item) => item._id === formData.categoryId);
    return category?.name || '';
  }, [categories, formData.categoryId]);

  const maxOfferDurationDays = subscription?.planId?.maxAdsDurationDays || 10;

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/mobile/categories');
      setCategories(response.data.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error fetching categories' });
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/api/shop/subscription');
      setSubscription(response.data.data || null);
    } catch (error) {
      setSubscription(null);
    }
  };

  const fetchAds = async () => {
    try {
      const params = filters.status ? { status: filters.status } : {};
      const response = await api.get('/api/shop/ads', { params });
      setAds(response.data.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error fetching ads' });
    } finally {
      setLoading(false);
    }
  };

  const resetImages = () => {
    setMainImageFile(null);
    setMainImagePreview('');
    setMainImageCrop(defaultCrop);
    setProductImageFile(null);
    setProductImagePreview('');
    setProductImageCrop(defaultCrop);
    setSubImageFiles([]);
    setSubImagePreviews([]);
  };

  const handleCreate = () => {
    setEditingAd(null);
    setFormData({
      ...createEmptyForm(),
      durationDays: Math.min(10, maxOfferDurationDays)
    });
    setProductSearch('');
    setProductSuggestions([]);
    resetImages();
    setMessage({ type: '', text: '' });
    setModalMessage({ type: '', text: '' });
    setShowModal(true);
  };

  const handleEdit = async (ad) => {
    setEditingAd(ad);
    setFormData({
      ...createEmptyForm(),
      adType: ad.isProductOffer ? 'product' : 'poster',
      categoryId: ad.categoryId?._id || ad.categoryId || '',
      productId: ad.productId?._id || ad.productId || '',
      productName: ad.productId?.name || '',
      title: ad.title || '',
      description: ad.description || '',
      mainImageUrl: ad.mainImageUrl || '',
      posterImage: ad.posterImage || ad.mainImageUrl || '',
      productImage: ad.productImage || '',
      subImageUrls: Array.isArray(ad.subImageUrls) ? ad.subImageUrls.join('\n') : '',
      originalPrice: ad.originalPrice ?? '',
      offerPrice: ad.offerPrice ?? '',
      durationDays: ad.durationDays || 10,
      startDate: '',
      productItems: ad.productId?.name ? [{
        ...emptyProductItem(),
        name: ad.productId.name,
        image: ad.productImage || ad.productId.image || ''
      }] : [emptyProductItem()]
    });
    setMainImageFile(null);
    setMainImagePreview(ad.posterImage || ad.mainImageUrl || '');
    setMainImageCrop(defaultCrop);
    setProductImageFile(null);
    setProductImagePreview(ad.productImage || '');
    setProductImageCrop(defaultCrop);
    setSubImageFiles([]);
    setSubImagePreviews(ad.subImageUrls || []);
    setModalMessage({ type: '', text: '' });
    setShowModal(true);

    if (ad.isProductOffer) {
      try {
        const response = await api.get(`/api/shop/ads/${ad._id}/products`);
        const linkedProducts = response.data.data || [];
        if (linkedProducts.length) {
          setFormData((current) => ({
            ...current,
            productItems: linkedProducts.map(productToFormItem)
          }));
        }
      } catch (error) {
        setModalMessage({ type: 'error', text: 'Could not load linked products for this offer' });
      }
    }
  };

  const handleImageChange = async (event, type) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const preview = await readFilePreview(file);

    if (type === 'product') {
      setProductImageFile(file);
      setProductImagePreview(preview);
      setProductImageCrop(defaultCrop);
      return;
    }

    setMainImageFile(file);
    setMainImagePreview(preview);
    setMainImageCrop(defaultCrop);
  };

  const usePosterAsProductCropSource = () => {
    if (!mainImageFile || !mainImagePreview) {
      setModalMessage({
        type: 'error',
        text: 'Upload a poster image first, then crop the product from it.'
      });
      return;
    }

    setProductImageFile(mainImageFile);
    setProductImagePreview(mainImagePreview);
    setProductImageCrop({
      enabled: true,
      x: 25,
      y: 25,
      width: 35,
      height: 35
    });
    setFormData((current) => ({
      ...current,
      productImage: ''
    }));
    setModalMessage({
      type: 'success',
      text: 'Poster image loaded as product crop source. Adjust the custom crop box around the product.'
    });
  };

  const handleSubImagesChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSubImageFiles(files);
    setSubImagePreviews(await Promise.all(files.map(readFilePreview)));
  };

  const fetchProductSuggestions = async () => {
    if (!productSearch.trim()) {
      setProductSuggestions([]);
      return;
    }

    try {
      const response = await api.get('/api/products/catalog/search', {
        params: { q: productSearch.trim(), limit: 8 }
      });
      setProductSuggestions(response.data.data || []);
    } catch (error) {
      setModalMessage({ type: 'error', text: 'Product search failed' });
    }
  };

  const selectProduct = (product) => {
    setFormData((current) => ({
      ...current,
      adType: 'product',
      productId: product._id,
      productName: product.name,
      categoryId: product.categoryId || current.categoryId,
      productImage: product.image || current.productImage,
      title: current.title || product.name,
      productItems: current.productItems.map((item, index) => (
        index === 0
          ? { ...item, name: product.name, image: product.image || item.image }
          : item
      ))
    }));
    setProductSearch(product.name);
    setProductSuggestions([]);
    if (product.image) setProductImagePreview(product.image);
  };

  const updateProductItem = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      productId: '',
      productItems: current.productItems.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: value } : item
      ))
    }));
  };

  const addProductItem = () => {
    setFormData((current) => ({
      ...current,
      productId: '',
      productItems: [...current.productItems, emptyProductItem()]
    }));
  };

  const removeProductItem = (index) => {
    setFormData((current) => ({
      ...current,
      productId: '',
      productItems: current.productItems.length > 1
        ? current.productItems.filter((_, itemIndex) => itemIndex !== index)
        : [emptyProductItem()]
    }));
  };

  const buildProductItemsPayload = () => formData.productItems
    .map((item) => ({
      name: item.name.trim(),
      unit: item.unit.trim(),
      price: item.price === '' ? undefined : Number(item.price),
      originalPrice: item.originalPrice === '' ? undefined : Number(item.originalPrice),
      offerPrice: item.offerPrice === '' ? undefined : Number(item.offerPrice),
      image: item.image.trim(),
      keywords: splitValues(item.keywords),
      tags: splitValues(item.tags)
    }))
    .filter((item) => item.name);

  const uploadSelectedImages = async (payload) => {
    const finalPayload = { ...payload };

    if (mainImageFile) {
      const croppedFile = await cropImageFile(mainImageFile, mainImageCrop);
      const uploadResult = await uploadFile(croppedFile, 'single');
      if (!uploadResult.success) throw new Error(uploadResult.message || 'Error uploading poster image');
      finalPayload.mainImageUrl = uploadResult.url;
      finalPayload.posterImage = uploadResult.url;
    } else if (!finalPayload.mainImageUrl && !finalPayload.posterImage && formData.adType === 'poster' && !editingAd) {
      throw new Error('Please upload a poster image or provide an image URL');
    }

    if (productImageFile) {
      const croppedFile = await cropImageFile(productImageFile, productImageCrop);
      const uploadResult = await uploadFile(croppedFile, 'single');
      if (!uploadResult.success) throw new Error(uploadResult.message || 'Error uploading product image');
      finalPayload.productImage = uploadResult.url;
    }

    if (subImageFiles.length > 0) {
      const uploadResult = await uploadMultipleFiles(subImageFiles);
      if (!uploadResult.success) throw new Error(uploadResult.message || 'Error uploading sub images');
      finalPayload.subImageUrls = uploadResult.urls;
    } else if (typeof finalPayload.subImageUrls === 'string') {
      finalPayload.subImageUrls = finalPayload.subImageUrls.split('\n').map((url) => url.trim()).filter(Boolean);
    } else if (!Array.isArray(finalPayload.subImageUrls)) {
      finalPayload.subImageUrls = [];
    }

    return finalPayload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setUploading(true);
    setModalMessage({ type: '', text: '' });

    try {
      const isProductOffer = formData.adType === 'product';
      const productItemsPayload = isProductOffer ? buildProductItemsPayload() : [];
      let payload = {
        ...formData,
        isProductOffer,
        productId: isProductOffer ? formData.productId || undefined : undefined,
        productName: isProductOffer && !formData.productId && !productItemsPayload.length ? formData.productName : undefined,
        productItems: isProductOffer ? productItemsPayload : undefined,
        productKeywords: splitValues(formData.productKeywords),
        productTags: splitValues(formData.productTags),
        originalPrice: formData.originalPrice === '' ? undefined : Number(formData.originalPrice),
        offerPrice: formData.offerPrice === '' ? undefined : Number(formData.offerPrice),
        durationDays: Number(formData.durationDays || 10)
      };

      if (isProductOffer && !payload.productId && !payload.productName && !productItemsPayload.length) {
        throw new Error('Select a product or add at least one product name');
      }

      payload = await uploadSelectedImages(payload);

      if (!payload.mainImageUrl && payload.productImage) {
        payload.mainImageUrl = payload.productImage;
        payload.posterImage = payload.productImage;
      }

      if (editingAd) {
        await api.put(`/api/shop/ads/${editingAd._id}`, payload);
        setMessage({ type: 'success', text: 'Ad updated successfully' });
      } else {
        await api.post('/api/shop/ads', payload);
        setMessage({ type: 'success', text: isProductOffer ? 'Product offer created successfully' : 'Ad created successfully' });
      }

      setShowModal(false);
      resetImages();
      fetchAds();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error saving ad';
      setModalMessage({ type: 'error', text: errorMessage });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;

    try {
      await api.delete(`/api/shop/ads/${id}`);
      setMessage({ type: 'success', text: 'Ad deleted successfully' });
      fetchAds();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting ad' });
    }
  };

  const handleBoost = async (adId) => {
    const days = window.prompt('Enter boost duration in days (default: 7):', '7');
    if (!days) return;

    try {
      await api.post(`/api/shop/ads/${adId}/boost`, { boostDurationDays: parseInt(days, 10) });
      setMessage({ type: 'success', text: 'Ad boosted successfully' });
      fetchAds();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error boosting ad' });
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <div className="container shop-ads-page">
        <div className="page-header">
          <h1 className="page-title">Ads & Product Offers</h1>
          <button className="btn btn-primary" onClick={handleCreate}>
            Create Offer
          </button>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
            {message.text}
          </div>
        )}

        <div className="filters">
          <div className="form-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="scheduled">Scheduled</option>
              <option value="expired">Expired</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
        </div>

        <div className="card table-card">
          <table className="table responsive-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Category</th>
                <th>Status</th>
                <th>Boosted</th>
                <th>End Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ads.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-table-cell">No ads found</td>
                </tr>
              ) : ads.map((ad) => (
                <tr key={ad._id}>
                  <td data-label="Title">{ad.title}</td>
                  <td data-label="Type">{ad.isProductOffer ? 'Product Offer' : 'Poster Ad'}</td>
                  <td data-label="Category">{ad.categoryId?.name || 'N/A'}</td>
                  <td data-label="Status">{ad.status}</td>
                  <td data-label="Boosted">{ad.isBoosted ? 'Yes' : 'No'}</td>
                  <td data-label="End Date">{ad.endDate ? new Date(ad.endDate).toLocaleDateString() : 'N/A'}</td>
                  <td data-label="Actions">
                    <div className="action-row">
                      <Link to={`/shop/ads/${ad._id}`} className="btn btn-secondary">View</Link>
                      <button className="btn btn-secondary" onClick={() => handleEdit(ad)}>Edit</button>
                      {ad.status === 'active' && !ad.isBoosted && (
                        <button className="btn btn-success" onClick={() => handleBoost(ad._id)}>Boost</button>
                      )}
                      <button className="btn btn-danger" onClick={() => handleDelete(ad._id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal shop-ad-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">{editingAd ? 'Edit Offer' : 'Create Offer'}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>x</button>
              </div>

              {modalMessage.text && (
                <div className={`alert alert-${modalMessage.type === 'error' ? 'error' : 'success'} modal-alert`}>
                  {modalMessage.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="shop-ad-form">
                <div className="offer-type-toggle" aria-label="Offer type">
                  <button
                    type="button"
                    className={formData.adType === 'poster' ? 'active' : ''}
                    onClick={() => setFormData({ ...formData, adType: 'poster', isProductOffer: false })}
                  >
                    Poster Ad
                  </button>
                  <button
                    type="button"
                    className={formData.adType === 'product' ? 'active' : ''}
                    onClick={() => setFormData({ ...formData, adType: 'product', isProductOffer: true })}
                  >
                    Product Offer
                  </button>
                </div>

                <div className="form-grid-two">
                  <div className="form-group">
                    <label>Category {formData.adType === 'poster' ? '*' : ''}</label>
                    <select
                      value={formData.categoryId}
                      onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                      required={formData.adType === 'poster'}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Duration (Days, max {maxOfferDurationDays}) *</label>
                    <input
                      type="number"
                      value={formData.durationDays}
                      onChange={(e) => setFormData({ ...formData, durationDays: e.target.value })}
                      required
                      min="1"
                      max={maxOfferDurationDays}
                    />
                  </div>
                </div>

                {formData.adType === 'product' && (
                  <div className="product-offer-box">
                    <div className="form-group">
                      <label>Search Product</label>
                      <div className="search-inline">
                        <input
                          type="text"
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          placeholder="onion, rice, oil"
                        />
                        <button type="button" className="btn btn-secondary" onClick={fetchProductSuggestions}>Search</button>
                      </div>
                      {productSuggestions.length > 0 && (
                        <div className="product-suggestion-list">
                          {productSuggestions.map((product) => (
                            <button key={product._id} type="button" onClick={() => selectProduct(product)}>
                              {product.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Selected Catalog Product</label>
                      <input type="text" value={formData.productId ? formData.productName : 'New shop products'} disabled />
                    </div>

                    <div className="form-group">
                      <label>Offer Products *</label>
                      <div className="product-items-table">
                        <div className="product-item-row product-item-head">
                          <span>Product Name</span>
                          <span>Unit</span>
                          <span>Original Price</span>
                          <span>Offer Price</span>
                          <span></span>
                        </div>
                        {formData.productItems.map((item, index) => (
                          <div className="product-item-row" key={index}>
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => updateProductItem(index, 'name', e.target.value)}
                              placeholder="Onion"
                              required={formData.adType === 'product' && !formData.productId && index === 0}
                            />
                            <input
                              type="text"
                              value={item.unit}
                              onChange={(e) => updateProductItem(index, 'unit', e.target.value)}
                              placeholder="1 kg"
                            />
                            <input
                              type="number"
                              min="0"
                              value={item.originalPrice}
                              onChange={(e) => updateProductItem(index, 'originalPrice', e.target.value)}
                            />
                            <input
                              type="number"
                              min="0"
                              value={item.offerPrice}
                              onChange={(e) => updateProductItem(index, 'offerPrice', e.target.value)}
                            />
                            <button type="button" className="btn btn-secondary" onClick={() => removeProductItem(index)}>
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                      <button type="button" className="btn btn-secondary add-product-row" onClick={addProductItem}>
                        Add Product
                      </button>
                    </div>

                    <div className="form-grid-two">
                      <div className="form-group">
                        <label>Original Price</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.originalPrice}
                          onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Offer Price</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.offerPrice}
                          onChange={(e) => setFormData({ ...formData, offerPrice: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="form-grid-two">
                      <div className="form-group">
                        <label>Product Keywords</label>
                        <input
                          type="text"
                          value={formData.productKeywords}
                          onChange={(e) => setFormData({ ...formData, productKeywords: e.target.value })}
                          placeholder={selectedCategoryName ? `${selectedCategoryName}, offer` : 'onion, grocery'}
                        />
                      </div>
                      <div className="form-group">
                        <label>Product Tags</label>
                        <input
                          type="text"
                          value={formData.productTags}
                          onChange={(e) => setFormData({ ...formData, productTags: e.target.value })}
                          placeholder="red onion, small onion"
                        />
                      </div>
                    </div>

                    <ImagePicker
                      title="Product Image"
                      filePreview={productImagePreview}
                      crop={productImageCrop}
                      onCropChange={setProductImageCrop}
                      onFileChange={(event) => handleImageChange(event, 'product')}
                      urlValue={formData.productImage}
                      onUrlChange={(value) => {
                        setFormData({ ...formData, productImage: value });
                        setProductImagePreview(value);
                      }}
                      helperAction={mainImageFile ? (
                        <button type="button" className="btn btn-secondary" onClick={usePosterAsProductCropSource}>
                          Crop Product From Poster
                        </button>
                      ) : null}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <ImagePicker
                  title={formData.adType === 'product' ? 'Poster Image' : 'Main Image *'}
                  filePreview={mainImagePreview}
                  crop={mainImageCrop}
                  onCropChange={setMainImageCrop}
                  onFileChange={(event) => handleImageChange(event, 'main')}
                  urlValue={formData.mainImageUrl || formData.posterImage}
                  onUrlChange={(value) => {
                    setFormData({ ...formData, mainImageUrl: value, posterImage: value });
                    setMainImagePreview(value);
                  }}
                  helperAction={formData.adType === 'product' && mainImageFile ? (
                    <button type="button" className="btn btn-secondary" onClick={usePosterAsProductCropSource}>
                      Use This Poster To Crop Product
                    </button>
                  ) : null}
                />

                <div className="form-group">
                  <label>Sub Images</label>
                  <input type="file" accept="image/*" multiple onChange={handleSubImagesChange} />
                  {subImagePreviews.length > 0 && (
                    <div className="sub-image-grid">
                      {subImagePreviews.map((preview, index) => (
                        <img key={`${preview}-${index}`} src={preview} alt="" />
                      ))}
                    </div>
                  )}
                  {subImageFiles.length === 0 && (
                    <textarea
                      value={formData.subImageUrls}
                      onChange={(e) => setFormData({ ...formData, subImageUrls: e.target.value })}
                      placeholder="Image URLs, one per line"
                      rows={3}
                    />
                  )}
                </div>

                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary" disabled={uploading}>
                    {uploading ? 'Uploading...' : (editingAd ? 'Update Offer' : 'Create Offer')}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ImagePicker = ({ title, filePreview, crop, onCropChange, onFileChange, urlValue, onUrlChange, helperAction }) => {
  const [dragState, setDragState] = useState(null);
  const currentCrop = normalizeCrop(crop);

  const updateCrop = (nextCrop) => {
    onCropChange(normalizeCrop({ ...currentCrop, ...nextCrop }));
  };

  const getPointerPercent = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100),
      y: clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100)
    };
  };

  const resizeCrop = (handle, point, startCrop) => {
    let { x, y, width, height } = startCrop;
    const right = startCrop.x + startCrop.width;
    const bottom = startCrop.y + startCrop.height;

    if (handle.includes('w')) {
      x = clamp(point.x, 0, right - 5);
      width = right - x;
    }
    if (handle.includes('e')) {
      width = clamp(point.x - startCrop.x, 5, 100 - startCrop.x);
    }
    if (handle.includes('n')) {
      y = clamp(point.y, 0, bottom - 5);
      height = bottom - y;
    }
    if (handle.includes('s')) {
      height = clamp(point.y - startCrop.y, 5, 100 - startCrop.y);
    }

    return normalizeCrop({ enabled: true, x, y, width, height });
  };

  const handlePointerDown = (event) => {
    if (!filePreview) return;

    const point = getPointerPercent(event);
    const handle = event.target.dataset.handle;
    const action = handle ? 'resize' : event.target.dataset.action === 'move' ? 'move' : 'select';

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    setDragState({
      action,
      handle,
      startPoint: point,
      startCrop: currentCrop
    });

    if (action === 'select') {
      onCropChange({
        enabled: true,
        x: point.x,
        y: point.y,
        width: 5,
        height: 5
      });
    }
  };

  const handlePointerMove = (event) => {
    if (!dragState) return;

    const point = getPointerPercent(event);

    if (dragState.action === 'select') {
      const x = Math.min(dragState.startPoint.x, point.x);
      const y = Math.min(dragState.startPoint.y, point.y);
      const width = Math.abs(point.x - dragState.startPoint.x);
      const height = Math.abs(point.y - dragState.startPoint.y);

      onCropChange(normalizeCrop({
        enabled: true,
        x,
        y,
        width: Math.max(5, width),
        height: Math.max(5, height)
      }));
      return;
    }

    if (dragState.action === 'move') {
      const nextX = dragState.startCrop.x + point.x - dragState.startPoint.x;
      const nextY = dragState.startCrop.y + point.y - dragState.startPoint.y;
      onCropChange(normalizeCrop({
        ...dragState.startCrop,
        enabled: true,
        x: clamp(nextX, 0, 100 - dragState.startCrop.width),
        y: clamp(nextY, 0, 100 - dragState.startCrop.height)
      }));
      return;
    }

    onCropChange(resizeCrop(dragState.handle, point, dragState.startCrop));
  };

  const handlePointerUp = (event) => {
    if (!dragState) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setDragState(null);
  };

  return (
    <div className="form-group image-picker">
      <div className="image-picker-header">
        <label>{title}</label>
        {helperAction}
      </div>
      <input type="file" accept="image/*" onChange={onFileChange} />

      {filePreview && (
        <div
          className={`custom-crop-preview ${currentCrop.enabled ? 'is-cropping' : ''}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <img src={filePreview} alt="" draggable="false" />
          {currentCrop.enabled && (
            <div
              className="custom-crop-box"
              data-action="move"
              style={{
                left: `${currentCrop.x}%`,
                top: `${currentCrop.y}%`,
                width: `${currentCrop.width}%`,
                height: `${currentCrop.height}%`
              }}
            >
              {['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map((handle) => (
                <span key={handle} className={`crop-handle crop-handle-${handle}`} data-handle={handle} />
              ))}
            </div>
          )}
        </div>
      )}

      {filePreview && (
        <div className="crop-controls crop-mouse-controls">
          <label>
            <span>Custom Crop</span>
            <input
              type="checkbox"
              checked={currentCrop.enabled}
              onChange={(e) => updateCrop({ enabled: e.target.checked })}
            />
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => updateCrop({ enabled: true, x: 20, y: 20, width: 40, height: 40 })}
          >
            Add Crop Box
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onCropChange(defaultCrop)}
          >
            Reset Crop
          </button>
        </div>
      )}

      <input
        type="url"
        value={urlValue || ''}
        onChange={(e) => onUrlChange(e.target.value)}
        placeholder="Or paste image URL"
      />
    </div>
  );
};

export default ShopAdminAds;
